import React from 'react';
import { useSEO } from '../../../hooks/useSEO';
import { ER_CONTENT as C } from './content';
import WhatsAppCTA from '../SoftwareForDistributors/components/WhatsAppCTA';
import SectionFAQ from '../SoftwareForDistributors/components/SectionFAQ';
import SectionDifferentials from '../SoftwareForDistributors/components/SectionDifferentials';
import './EquipmentRentalLanding.scss';

const EquipmentRentalLanding: React.FC = () => {
  useSEO({
    title: C.seo.title,
    description: C.seo.description,
    canonical: '/sistema-para-locadora-de-equipamentos',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: '77Gestão — Sistema para Locadora de Equipamentos',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://77gestao.com.br/sistema-para-locadora-de-equipamentos',
      description: C.seo.description,
    },
  });

  return (
    <div className="equipment-rental-landing">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="lp-hero" aria-labelledby="er-hero-title">
        <div className="lp-container">
          <span className="lp-kicker">{C.hero.kicker}</span>
          <div className="lp-hero-title-wrapper">
            <h1 className="lp-title" id="er-hero-title">{C.hero.title}</h1>
            <div className="lp-hero-icon">
              <i className="fa-solid fa-toolbox" aria-hidden="true" />
            </div>
          </div>
          <p className="lp-subtitle">{C.hero.subtitle}</p>
          <WhatsAppCTA message={C.hero.ctaMessage} id="cta-hero">
            {C.hero.cta}
          </WhatsAppCTA>
          <p className="lp-meta">{C.hero.urgency}</p>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────────────── */}
      <div className="er-stats-strip" aria-hidden="true">
        <div className="lp-container">
          {C.statsStrip.map((s, i) => (
            <div className="er-stat" key={i}>
              <i className={s.icon} />
              <span className="er-stat__number">{s.number}</span>
              <span className="er-stat__label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pain ─────────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--pain" aria-labelledby="er-pain-title">
        <div className="lp-container">
          <h2 id="er-pain-title">{C.pain.title}</h2>
          <p className="lp-intro">{C.pain.intro}</p>
          <ul className="er-pain-list">
            {C.pain.items.map((item, i) => (
              <li key={i}>
                <i className={item.icon} aria-hidden="true" />
                {item.text}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Mid CTA ──────────────────────────────────────────────────── */}
      <div className="er-mid-cta">
        <div className="lp-container">
          <p>{C.midCta.text}</p>
          <WhatsAppCTA message={C.midCta.ctaMessage} id="cta-mid">
            {C.midCta.cta}
          </WhatsAppCTA>
        </div>
      </div>

      {/* ── Solution ─────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--solution" aria-labelledby="er-solution-title">
        <div className="lp-container">
          <h2 id="er-solution-title">{C.solution.title}</h2>
          <p className="lp-intro">{C.solution.intro}</p>
          <div className="lp-cards">
            {C.solution.cards.map((card, i) => (
              <article className="lp-card" key={i}>
                <div className="lp-card__icon">
                  <i className={card.icon} aria-hidden="true" />
                </div>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Proof ────────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--proof" aria-labelledby="er-proof-title">
        <div className="lp-container">
          <h2 id="er-proof-title">{C.proof.title}</h2>
          <p className="lp-intro">{C.proof.intro}</p>

          <div className="er-proof-stats">
            {C.proof.stats.map((s, i) => (
              <div className="er-proof-stat" key={i}>
                <span className="er-proof-stat__number">{s.number}</span>
                <span className="er-proof-stat__label">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="lp-proof-box">
            {C.proof.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            <p className="lp-proof-highlight">
              <i className="fa-solid fa-check-circle" aria-hidden="true" />
              {C.proof.highlight}
            </p>
          </div>

          <div className="lp-section-cta">
            <WhatsAppCTA message={C.proof.ctaMessage} id="cta-proof">
              {C.proof.cta}
            </WhatsAppCTA>
          </div>
        </div>
      </section>

      {/* ── Benefits ─────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--benefits" aria-labelledby="er-benefits-title">
        <div className="lp-container">
          <h2 id="er-benefits-title">{C.benefits.title}</h2>
          <div className="lp-benefits-grid">
            {C.benefits.items.map((item, i) => (
              <article key={i}>
                <div className="lp-benefit__icon">
                  <i className={item.icon} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Differentials ────────────────────────────────────────────── */}
      <SectionDifferentials
        {...C.differentials}
        cta={{ text: 'Falar com a equipe', message: C.hero.ctaMessage }}
      />

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <SectionFAQ {...C.faq} />

      {/* ── CTA Final ────────────────────────────────────────────────── */}
      <section className="lp-cta-final" id="schedule-demo" aria-labelledby="er-cta-final-title">
        <div className="lp-container">
          <h2 id="er-cta-final-title">{C.ctaFinal.title}</h2>
          <p>{C.ctaFinal.subtitle}</p>
          <WhatsAppCTA message={C.ctaFinal.ctaMessage} className="lp-btn lp-btn--final" id="cta-final">
            {C.ctaFinal.cta}
          </WhatsAppCTA>
        </div>
      </section>
    </div>
  );
};

export default EquipmentRentalLanding;
